import { useGetUserQuery } from "features/auth/auth";
import Layout from "../components/Layout";
import { CircularProgress, Container, Box, Typography, IconButton, Snackbar, Alert, Breadcrumbs, Link } from "@mui/material";
import { ArrowBack, Home } from "@mui/icons-material";
import FileList from "../components/fileStorage/FileList";
import FileSearch from "../components/fileStorage/FileSearch";
import FolderTypeFilter from "../components/fileStorage/FolderTypeFilter";
import { useState, useEffect, useCallback } from "react";
import { FileItem, FolderType } from "../types/interfaces/filesStorage";
import { useGetFileSystemQuery } from "features/fileSystem/filesStorage";
import { useDownloadManager } from "../hooks/useDownloadManager";
import { paginationStorage, folderMappingsStorage } from "../utils/localStorage";
import { storagePaginationValues } from "../utils/constants";
import { useSearchParams, useNavigate } from "react-router-dom";

export default function HomePage() {
  const { isLoading: userLoading } = useGetUserQuery(undefined, {});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

    // Get initial values from URL params (excluding limit which is in localStorage)
  const getInitialState = useCallback(() => {
    const prefix = searchParams.get('prefix') || '';
    const page = parseInt(searchParams.get('page') || '1');
    const search = searchParams.get('search') || null;
    const folderType = (searchParams.get('folder_type') as FolderType) || 'all';

    return { prefix, page, search, folderType };
  }, [searchParams]);

    // Initialize currentLimit from localStorage only
  const [currentLimit, setCurrentLimit] = useState(() => {
    const savedLimit = paginationStorage.getPaginationValue();
    const validLimit = storagePaginationValues.includes(savedLimit) ? savedLimit : 25;
    return validLimit;
  });

  const [currentPrefix, setCurrentPrefix] = useState(getInitialState().prefix);
  const [currentSearch, setCurrentSearch] = useState<string | null>(getInitialState().search);
  const [currentFolderType, setCurrentFolderType] = useState<FolderType>(getInitialState().folderType);
  const [currentContinuationToken, setCurrentContinuationToken] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(getInitialState().page);
  const [pageTokens, setPageTokens] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState<boolean>(true);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Custom download manager hook
  const downloadManager = useDownloadManager();

    // Update URL with new state (excluding limit which is stored in localStorage)
  const updateURL = useCallback((newState: { prefix?: string; page?: number; search?: string | null; folderType?: FolderType }) => {
    const currentState = getInitialState();
    const updatedState = { ...currentState, ...newState };

    // Create new search params
    const params = new URLSearchParams();
    if (updatedState.prefix) params.set('prefix', updatedState.prefix);
    if (updatedState.page > 1) params.set('page', updatedState.page.toString());
    if (updatedState.search) params.set('search', updatedState.search);
    if (updatedState.folderType && updatedState.folderType !== 'all') params.set('folder_type', updatedState.folderType);

    setSearchParams(params);
  }, [getInitialState, setSearchParams]);

  // Get file system data using the API
  const { data: fileSystemData, isLoading: fileSystemLoading, error: fileSystemError, isFetching: fileSystemFetching } = useGetFileSystemQuery({
    prefix: currentPrefix,
    continuation_token: currentContinuationToken,
    search: currentSearch,
    limit: currentLimit,
    folder_type: currentFolderType,
  });

  useEffect(() => {
    if (fileSystemData?.show_filter !== null && fileSystemData?.show_filter !== undefined) {
      setShowFilter(fileSystemData.show_filter);
    }
  }, [fileSystemData?.show_filter]);

  // Log when we reach the last page
  useEffect(() => {
    if (fileSystemData && !fileSystemData.continuation_token && currentPage > 1) {
      // Reached last page - no more data available
    }
  }, [fileSystemData?.continuation_token, currentPage]);

  const handleItemClick = (item: FileItem) => {
    if (item.ftype === "folder") {
      // Find the original item data to get the correct fname for storage
      const originalItem = fileSystemData?.folders.find(folder => folder.fsource === item.fsource);
      const fnameToStore = originalItem?.fname || item.fname;

      // Clear irrelevant mappings when navigating to a new folder
      folderMappingsStorage.clearIrrelevantMappings(item.fsource);

      // Store the fname/fsource pair in localStorage
      folderMappingsStorage.setFolderMapping(fnameToStore, item.fsource);

      // Navigate into folder using fsource as prefix
      setCurrentPrefix(item.fsource);
      setCurrentContinuationToken(null); // Reset pagination when entering folder
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ prefix: item.fsource, page: 1, search: null });
    } else {
      // Handle file click (could open preview, etc.)
    }
  };

      const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

    const handleDownloadClick = async (item: FileItem) => {
    // Find the original item data to ensure we have the correct fname for the download manager
    const originalItem = fileSystemData?.folders.find(folder => folder.fsource === item.fsource) || item;

    const result = await downloadManager.downloadItem(originalItem);

    if (result.success) {
      showNotification(result.message, 'success');
    } else {
      showNotification(result.message, 'error');
    }
  };

      const handleLimitChange = (limit: number) => {
    // Save to localStorage (limit is a user preference, not part of URL state)
    const saved = paginationStorage.setValidPaginationValue(limit, storagePaginationValues);
    if (!saved) {
      // Failed to save pagination value to localStorage
    }

    // Update state
    setCurrentLimit(limit);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ page: 1 }); // Only update page, limit stays in localStorage
  };

  const handlePageChange = (page: number) => {
    // Check if the requested page is valid
    const maxPages = fileSystemData?.continuation_token ? pageTokens.length + 1 : currentPage;

    if (page < 1 || page > maxPages) {
      return;
    }

    if (page === 1) {
      // First page - no continuation token
      setCurrentContinuationToken(null);
    } else if (page <= pageTokens.length + 1) {
      // Use the token from the previous page
      const tokenIndex = page - 2; // -2 because page 2 uses token from page 1
      setCurrentContinuationToken(pageTokens[tokenIndex]);
    } else {
      return;
    }

    setCurrentPage(page);
    updateURL({ page });
  };

  const handleSearch = useCallback((searchTerm: string | null) => {
    setCurrentSearch(searchTerm);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ search: searchTerm, page: 1 });
  }, [updateURL]);

  const handleFolderTypeChange = useCallback((folderType: FolderType) => {
    setCurrentFolderType(folderType);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ folderType, page: 1 });
  }, [updateURL]);

  // Track continuation tokens from API responses
  useEffect(() => {
    if (fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens(prev => {
        const newTokens = [...prev];
        // Store token for current page (will be used for next page)
        if (currentPage > newTokens.length) {
          newTokens.push(fileSystemData.continuation_token!);
        } else {
          newTokens[currentPage - 1] = fileSystemData.continuation_token!;
        }
        return newTokens;
      });
    } else if (!fileSystemData?.continuation_token && currentPage > 0) {
      // No continuation token means this is the last page
      // Remove any tokens beyond the current page
      setPageTokens(prev => {
        const newTokens = prev.slice(0, currentPage - 1);
        // No continuation token - removing tokens beyond current page
        return newTokens;
      });
    }
  }, [fileSystemData?.continuation_token, currentPage]);

    // Listen for URL changes (browser back/forward buttons)
  useEffect(() => {
    const currentState = getInitialState();

    // Update component state if URL params changed
                    if (currentState.prefix !== currentPrefix) {
                  setCurrentPrefix(currentState.prefix);
                  setCurrentContinuationToken(null);
                  setCurrentPage(1);
                  setPageTokens([]);
                }

                    if (currentState.page !== currentPage) {
                  setCurrentPage(currentState.page);
                }

                    if (currentState.search !== currentSearch) {
                  setCurrentSearch(currentState.search);
                  setCurrentPage(1);
                  setCurrentContinuationToken(null);
                  setPageTokens([]);
                }

                    if (currentState.folderType !== currentFolderType) {
                  setCurrentFolderType(currentState.folderType);
                  setCurrentPage(1);
                  setCurrentContinuationToken(null);
                  setPageTokens([]);
                }
  }, [searchParams, getInitialState, currentPrefix, currentPage, currentSearch, currentFolderType]);

  const handleGoBack = () => {
    if (currentPrefix) {
      const pathParts = currentPrefix.split('/').filter(part => part);
      pathParts.pop(); // Remove last folder
      const newPrefix = pathParts.length > 0 ? pathParts.join('/') : '';

      // Check if the new prefix has a valid mapping (is a real folder)
      const hasValidMapping = folderMappingsStorage.getFname(newPrefix);

      if (!hasValidMapping && newPrefix !== '') {
        // The parent path doesn't have a mapping, so it's not a real folder
        // Go directly to root instead
        folderMappingsStorage.clearMappingsForHigherLevel('');
        setCurrentPrefix('');
        setCurrentContinuationToken(null);
        setCurrentPage(1);
        setPageTokens([]);
        updateURL({ prefix: '', page: 1 });
      } else {
        // The parent path is valid, navigate to it
        folderMappingsStorage.clearMappingsForHigherLevel(newPrefix);
        setCurrentPrefix(newPrefix);
        setCurrentContinuationToken(null);
        setCurrentPage(1);
        setPageTokens([]);
        updateURL({ prefix: newPrefix, page: 1 });
      }
    } else {
      // If no prefix, use browser back
      navigate(-1);
    }
  };

  // Build breadcrumb items for the current path
  const buildBreadcrumbs = useCallback(() => {
    const breadcrumbs = [];

    // Always add root/home breadcrumb
    breadcrumbs.push({
      fname: 'Home',
      fsource: '',
      isClickable: currentPrefix
    });

    // If no current prefix, just return home breadcrumb
    if (!currentPrefix) {
      return breadcrumbs;
    }

    const pathParts = currentPrefix.split('/').filter(part => part);

    // Build breadcrumbs for each path level
    let currentPath = '';
    for (let i = 0; i < pathParts.length; i++) {
      currentPath += (currentPath ? '/' : '') + pathParts[i];
      const fname = folderMappingsStorage.getFname(currentPath);
      // If no fname, skip this level (This folder is not in the mappings(not exists)
      if (!fname) {
        continue;
      }
      const isLast = i === pathParts.length - 1;

      breadcrumbs.push({
        fname,
        fsource: currentPath,
        isClickable: !isLast // Last item is not clickable (current level)
      });
    }

    return breadcrumbs;
  }, [currentPrefix]);

  // Handle breadcrumb click
  const handleBreadcrumbClick = (fsource: string) => {
    if (fsource === currentPrefix) {
      return; // Already at this level
    }

    try {
      // Clear mappings for higher level navigation (going up)
      folderMappingsStorage.clearMappingsForHigherLevel(fsource);

      setCurrentPrefix(fsource);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ prefix: fsource, page: 1 });
    } catch (error) {
      // Fallback to root if navigation fails
      setCurrentPrefix('');
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ prefix: '', page: 1 });
    }
  };


  if (userLoading) {
    return (
      <Layout title="File System" content="File System">
        <Container component="main" maxWidth="lg">
          <div
            style={{
              height: "100vh",
              backgroundColor: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="File System" content="File System">
      <Container
        component="main"
        maxWidth="lg"
        sx={{
          pt: 3,
          pb: 0,
          height: 'calc(100vh - 200px)', // Account for header and footer
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Search Bar with Filter */}
        <Box sx={{ mb: 3, flexShrink: 0, display: 'flex', gap: 2, alignItems: 'center' }}>
          {showFilter && (
            <FolderTypeFilter
              value={currentFolderType}
              onChange={handleFolderTypeChange}
              disabled={!!currentPrefix}
              disabledTooltip="Filter only available at root level"
            />
          )}
          <Box sx={{ flex: 1 }}>
            <FileSearch
              onSearch={handleSearch}
              placeholder="Search files and folders..."
            />
          </Box>
        </Box>

        {/* Navigation Header with Breadcrumbs */}
        <Box sx={{
          mb: 3,
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0
        }}>
          {currentPrefix && (
            <IconButton
              onClick={handleGoBack}
              size="small"
              sx={{
                backgroundColor: 'background.paper',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <Breadcrumbs aria-label="breadcrumb" separator="/">
            {buildBreadcrumbs().map((crumb) => (
              <Link
                key={crumb.fsource}
                underline="hover"
                color={crumb.isClickable ? "inherit" : "text.primary"}
                onClick={(e) => {
                  e.preventDefault();
                  if (crumb.isClickable) {
                    handleBreadcrumbClick(crumb.fsource);
                  }
                }}
                sx={{
                  cursor: crumb.isClickable ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: crumb.isClickable ? 'normal' : 'bold',
                  '&:hover': {
                    color: crumb.isClickable ? 'primary.main' : 'text.primary'
                  }
                }}
              >
                {crumb.fsource === '' && <Home fontSize="small" />}
                {crumb.fname}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>

        {/* File List */}
        <Box sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0
        }}>
          <FileList
            items={(fileSystemData?.folders || []).map(item => {
              // For folders, use the stored fname if available, otherwise use the original fname
              if (item.ftype === "folder") {
                const storedFname = folderMappingsStorage.getFname(item.fsource);
                return {
                  ...item,
                  fname: storedFname || item.fname
                };
              }
              return item;
            })}
            loading={fileSystemLoading || fileSystemFetching}
            error={fileSystemError ? "Failed to load files" : null}
            currentLimit={currentLimit}
            onLimitChange={handleLimitChange}
            onItemClick={handleItemClick}
            onDownloadClick={handleDownloadClick}
            showDownloadButton={true}
            showPagination={true}
            size="medium"
            variant="default"
            maxHeight="100%"
            currentPage={currentPage}
            totalPages={fileSystemData?.continuation_token ? pageTokens.length + 1 : currentPage}
            onPageChange={handlePageChange}
            pageTokens={pageTokens}
            downloadingItems={downloadManager.downloadingItems}
            downloadProgress={downloadManager.downloadProgress}
            hasNextPage={!!fileSystemData?.continuation_token}
          />
        </Box>
      </Container>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
